import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Crown, FileDown, Sparkles, Users, User } from 'lucide-react';

interface DownloadUpgradeModalProps {
  isOpen: boolean;
  onClose: () => void;
  recommendedPlan: 'Individual' | 'Corporate';
}

export const DownloadUpgradeModal = ({ isOpen, onClose, recommendedPlan }: DownloadUpgradeModalProps) => {
  const navigate = useNavigate();

  const planInfo = useMemo(() => {
    if (recommendedPlan === 'Corporate') {
      return {
        icon: <Users className="h-5 w-5 text-blue-600" />,
        description: 'Unlock team-wide exports, reporting workflows, and collaborative ESG operations.',
      };
    }

    return {
      icon: <User className="h-5 w-5 text-emerald-600" />,
      description: 'Unlock complete export features for your personal sustainability workflow.',
    };
  }, [recommendedPlan]);

  const handleUpgrade = () => {
    onClose();
    navigate('/settings?tab=advanced');
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Crown className="h-5 w-5 text-amber-500" />
            Upgrade Required For Downloads
          </DialogTitle>
          <DialogDescription>
            CSV, HTML, and PDF downloads are unavailable on the Basic plan.
          </DialogDescription>
        </DialogHeader>

        <Card className="border-amber-200 bg-gradient-to-br from-amber-50 to-white">
          <CardContent className="pt-6">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-white p-2 shadow-sm border">
                <FileDown className="h-5 w-5 text-gray-700" />
              </div>
              <div className="space-y-2">
                <p className="text-sm text-gray-700">
                  You attempted a protected export action. Upgrade to continue downloading reports and data files.
                </p>
                <div className="flex items-center gap-2">
                  <Badge className="bg-white text-gray-800 border">Recommended Plan</Badge>
                  <Badge className="bg-blue-600 text-white">{recommendedPlan}</Badge>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="border-blue-100">
          <CardContent className="pt-6 space-y-3">
            <div className="flex items-center gap-2 font-medium">
              {planInfo.icon}
              <span>{recommendedPlan} Plan</span>
            </div>
            <p className="text-sm text-gray-600">{planInfo.description}</p>
            <div className="flex items-center gap-2 text-sm text-gray-700">
              <Sparkles className="h-4 w-4 text-amber-500" />
              Includes CSV, HTML, and PDF export access.
            </div>
          </CardContent>
        </Card>

        <div className="flex gap-2">
          <Button variant="outline" className="flex-1" onClick={onClose}>
            Not Now
          </Button>
          <Button className="flex-1" onClick={handleUpgrade}>
            View {recommendedPlan} Plan
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
